import uniq from "lodash/uniq";
import flow from "lodash/fp/flow";
import map from "lodash/fp/map";
import flatten from "lodash/fp/flatten";
import filter from "lodash/fp/filter";
import {
  ConfigurationOption,
  ProductDetailGroupingSelect,
} from "../ProductDetailPageResult";
import { VirtualGroupingVariation } from "./VirtualGroupingModel";
import isMobile from "~/common/helpers/isMobile";

export const moveVirtualGroupingTile = (
  virtualGroupingVariationsRef: React.MutableRefObject<
    VirtualGroupingVariation[]
  >,
  virtualGroupMemberToDisplay: string | undefined,
) => {
  const indexOfSelectedVariation =
    virtualGroupingVariationsRef.current.findIndex(
      (item) => item.itemNumber === virtualGroupMemberToDisplay,
    );

  // If the selected variation tile is not in the first 8 tiles it will move it to the first tile in the list.
  if (indexOfSelectedVariation >= 8) {
    const variationItemToShift = virtualGroupingVariationsRef.current.find(
      (item) => item.itemNumber === virtualGroupMemberToDisplay,
    );

    virtualGroupingVariationsRef.current.splice(indexOfSelectedVariation, 1);

    virtualGroupingVariationsRef.current.unshift(
      variationItemToShift ?? {
        name: "",
        value: "",
        sortIndex: 0,
        previewImage: "",
        itemNumber: "",
        isInStock: false,
        price: {
          applyTypeLPriceToPlatinumPlus: false,
          displayMinimumAdvertisedPrice: false,
          displaySalePrice: false,
          hasLotQuantityDiscounts: false,
          hasQuantityDiscounts: false,
          hasSingleLotQuantityDiscount: false,
          minimumAdvertisedPriceOverride: {
            priceLevel5: 0,
          },
          minimumAdvertisedPriceProperties: {
            price: 0,
            type: "string",
          },
          price: 0,
          quantityDiscounts: [],
          salePrice: 0,
        },
        divideByUnit: "",
        divideByQuantity: "",
        showBelowMinimumAdvertisedPriceForLoggedInCustomers: false,
        showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: false,
        customerReviewSummary: "",
      },
    );
  }
};

// Gets the itemNumber of the virtual grouping product that is being selected
export const getItemNumber = (
  selectedOptions: ConfigurationOption[],
  productGroupingSelect: ProductDetailGroupingSelect[],
) => {
  const matchingItem = productGroupingSelect.find((variation) =>
    selectedOptions.every((selectedOption) =>
      variation.configurationOptions.some(
        (option) =>
          option.name === selectedOption.name &&
          option.value === selectedOption.value,
      ),
    ),
  );
  return matchingItem?.itemNumber;
};

// Gets the itemNumber from the virtual grouping url
export const getItemNumberFromUrl = (ev: PopStateEvent): string | null => {
  if (ev.state === null) {
    return null;
  }

  const itemNumber = ev.state?.itemNumber as string;

  return itemNumber;
};

// Creates the array of valid options used to ensure what is being selected is a valid virtual grouping product.
export const processValidOptions = (
  productGroupingSelect: ProductDetailGroupingSelect[],
  selectedOptionsToFilter: readonly ConfigurationOption[],
  name: string,
) => {
  return uniq(
    flow(
      filter(
        (variation: ProductDetailGroupingSelect) =>
          selectedOptionsToFilter.length === 0 ||
          selectedOptionsToFilter.every((selectedOption) =>
            variation.configurationOptions.some(
              (option) =>
                option.name === selectedOption.name &&
                option.value === selectedOption.value,
            ),
          ),
      ),
      map(
        (variation: ProductDetailGroupingSelect) =>
          variation.configurationOptions,
      ),
      flatten,
      filter((option) => option.name === name),
      map((option) => option.value),
    )(productGroupingSelect),
  );
};

export const showEightVirtualGroupTileVariations = (
  variations: VirtualGroupingVariation[],
) => {
  return variations.length > 8 && isMobile("600px")
    ? variations.slice(0, 8)
    : variations;
};

// Checks if the selected option is the same as the variation
export const findSelectedOption = (
  variation: VirtualGroupingVariation,
  selectedOptions: readonly ConfigurationOption[],
) => {
  return (
    selectedOptions.find((option) => option.name === variation.name)?.value ===
    variation.value
  );
};

export const getSortedOptions = <T extends { sortIndex: number }>(
  options: T[] | undefined,
): T[] | undefined => {
  if (options && options.length > 0) {
    return options.sort((a, b) => a.sortIndex - b.sortIndex);
  }

  return options;
};
